import { Action } from "redux";
import { ThunkAction } from "redux-thunk";
import { endpoints } from "..";
import { IAppState, ApiRequest } from "../../Common";

import { User } from "../../User";
import ResponseHandler from "../ResponseHandler";
import { ResponseResult } from "../../Common/Models/ResponseResult";

interface IAppAssignmentAction extends Action {
  type: AppAssignmentActionType;
  payload: any;
}

// ARGUMENT TYPES

// arguments for the getUsersAssignedToApp action creator
export interface IGetUsersAssignedToAppArguments {
  appId: string; // this is the app role prefix
}

// Arguments for the setErrored action creator
export interface ISetAppAssignedUsersErroredArguments {
  errorMessage: string;
  errorCode: number;
}

// PAYLOAD TYPES

// Payload for the APP_ASSIGNED_USERS_LOADED action
export interface IAppAssignedUsersLoadedPayload {
  loadedUsers: User[];
}

// Payload for the APP_ASSIGNED_USERS_ERRORED action
export interface ISetAppAssignedUsersErroredPayload {
  errorMessage: string;
  errorCode: number;
}

// Payload for the APP_ASSIGNED_USERS_UNLOAD action
export interface IUnloadAppAssignedUsersPayload {}
// Payload for the APP_ASSIGNED_USERS_LOADING action
export interface ISetAppAssignedUsersLoadingPayload {}

// ACTION TYPES

export interface IAppAssignedUsersLoadedAction extends IAppAssignmentAction {
  payload: IAppAssignedUsersLoadedPayload;
}

export interface IUnloadAppAssignedUsersAction extends IAppAssignmentAction {
  payload: IUnloadAppAssignedUsersPayload;
}

export interface ISetAppAssignedUsersErroredAction extends IAppAssignmentAction {
  payload: ISetAppAssignedUsersErroredPayload;
}

export interface ISetAppAssignedUsersLoadingAction extends IAppAssignmentAction {
  payload: ISetAppAssignedUsersLoadingPayload;
}

// Union of all action types. If you define a new action type, add it here please
export type AppAssignmentAction =
  | IAppAssignedUsersLoadedAction
  | IUnloadAppAssignedUsersAction
  | ISetAppAssignedUsersErroredAction
  | ISetAppAssignedUsersLoadingAction;

export enum AppAssignmentActionType {
  APP_ASSIGNED_USERS_LOADED = "APP_ASSIGNED_USERS_LOADED",
  APP_ASSIGNED_USERS_UNLOAD = "APP_ASSIGNED_USERS_UNLOAD",
  APP_ASSIGNED_USERS_ERRORED = "APP_ASSIGNED_USERS_ERRORED",
  APP_ASSIGNED_USERS_LOADING = "APP_ASSIGNED_USERS_LOADING"
}

// ACTION CREATORS

const setError = (status: number, description: string): ISetAppAssignedUsersErroredAction => {
  return {
    type: AppAssignmentActionType.APP_ASSIGNED_USERS_ERRORED,
    payload: {
      errorCode: status,
      errorMessage: description
    }
  };
};

export const unloadAppAssignedUsers = (): IUnloadAppAssignedUsersAction => ({
  type: AppAssignmentActionType.APP_ASSIGNED_USERS_UNLOAD,
  payload: {}
});
export const setAppAssignedUsersLoading = (): ISetAppAssignedUsersLoadingAction => ({
  type: AppAssignmentActionType.APP_ASSIGNED_USERS_LOADING,
  payload: {}
});
export const setAppAssignedUsersErrored = (
  args: ISetAppAssignedUsersErroredArguments
): ISetAppAssignedUsersErroredAction => ({
  type: AppAssignmentActionType.APP_ASSIGNED_USERS_ERRORED,
  payload: { ...args }
});

const assembleGetAssignedUsersUrl = (args: IGetUsersAssignedToAppArguments): string => {
  return endpoints.applications.getAssignedUsers(args.appId);
};

export const getUsersAssignedToApp = (
  args: IGetUsersAssignedToAppArguments
): ThunkAction<
  void,
  IAppState,
  {},
  IAppAssignedUsersLoadedAction | ISetAppAssignedUsersErroredAction
> => dispatch => {
  ApiRequest.fromPath(assembleGetAssignedUsersUrl(args))
    .withAuthHeader()
    .get()
    .then((response: Response) => {
      ResponseHandler.handle(
        response,
        (result: ResponseResult) => {
          dispatch({
            type: AppAssignmentActionType.APP_ASSIGNED_USERS_LOADED,
            payload: { loadedUsers: result.payload }
          });
        },
        (result: ResponseResult) => {
          dispatch(setError(response.status, result.error.message));
        },
        () => {
          dispatch(setError(response.status, "An unknown error has occured"));
        }
      );
    });
};
